// src/components/GiftCardCustomize.jsx
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Gift } from "lucide-react";
import MailChecker from "mailchecker";

export default function GiftCardCustomize() {
  const navigate = useNavigate();
  const location = useLocation();
  const giftCard = location.state?.giftCard;

  const [customization, setCustomization] = useState({
    senderName: "",
    senderEmail: "",
    recipientName: "",
    recipientEmail: "",
    personalMessage: "",
    deliveryDate: new Date().toISOString().split("T")[0],
  });

  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [discount, setDiscount] = useState(0);

  const [errors, setErrors] = useState({
    senderName: "",
    senderEmail: "",
    recipientName: "",
    recipientEmail: "",
  });

  if (!giftCard) {
    navigate("/gift-cards");
    return null;
  }

  // Round amount to 2 decimal places for display
  const displayAmount = giftCard.amount
    ? parseFloat(giftCard.amount).toFixed(2)
    : "0.00";

  // Calculate final amount after discount
  const originalAmount = parseFloat(giftCard.amount);
  const finalAmount = originalAmount - discount;

  // Promo code validation
  const handleApplyPromo = () => {
    const code = promoCode.trim().toUpperCase();

    // Check if promo expires on Dec 31, 2025
    const today = new Date();
    const expiryDate = new Date('2025-12-31T23:59:59');

    if (today > expiryDate) {
      setPromoError("This promo code has expired");
      setPromoApplied(false);
      setDiscount(0);
      return;
    }

    if (code === "HOLIDAYS") {
      // Check if amount is $100 or more
      if (originalAmount < 100) {
        setPromoError("This promo code is only valid for gift cards $100 or more");
        setPromoApplied(false);
        setDiscount(0);
        return;
      }

      // Apply 15% discount
      const discountAmount = originalAmount * 0.15;
      setDiscount(discountAmount);
      setPromoApplied(true);
      setPromoError("");
    } else if (code === "") {
      setPromoError("");
      setPromoApplied(false);
      setDiscount(0);
    } else {
      setPromoError("Invalid promo code");
      setPromoApplied(false);
      setDiscount(0);
    }
  };

  const validateEmail = (email) => {
    if (!email || email.trim() === "") {
      return { isValid: false, message: "" };
    }

    const trimmedEmail = email.trim();

    // Check for incomplete emails like "test@" or "asdasda@"
    if (trimmedEmail.includes("@") && !trimmedEmail.includes(".")) {
      return { isValid: false, message: "Please enter a valid email address." };
    }

    // Check if email ends with @ (like "test@" or "asdasda@")
    if (trimmedEmail.endsWith("@")) {
      return { isValid: false, message: "Please enter a valid email address." };
    }

    // Proper email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isFormatValid = emailRegex.test(trimmedEmail);

    if (!isFormatValid && trimmedEmail.length > 0) {
      return { isValid: false, message: "Please enter a valid email address." };
    }

    // Use mailchecker to validate email (checks format and blocks disposable emails)
    try {
      const isEmailValid = MailChecker.isValid(trimmedEmail);
      console.log("isEmailValid", isEmailValid);
      if (!isEmailValid) {
        return {
          isValid: false,
          message:
            "Please enter a valid email address. Disposable or temporary email addresses are not allowed.",
        };
      }
    } catch (error) {
      // If mailchecker fails, fall back to regex validation
      console.error("Email validation error:", error);
      return { isValid: isFormatValid, message: "" };
    }

    return { isValid: true, message: "" };
  };

  const validateName = (name) => {
    return name && name.trim().length > 0;
  };

  const handleChange = (field, value) => {
    setCustomization((prev) => ({ ...prev, [field]: value }));

    // Real-time validation for email fields
    if (field === "senderEmail" || field === "recipientEmail") {
      const emailValidation = validateEmail(value);
      if (value && !emailValidation.isValid) {
        setErrors((prev) => ({
          ...prev,
          [field]:
            emailValidation.message || "Please enter a valid email address.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    } else if (field === "senderName" || field === "recipientName") {
      // Clear error when user starts typing for name fields
      if (errors[field] && value.trim().length > 0) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {
      senderName: "",
      senderEmail: "",
      recipientName: "",
      recipientEmail: "",
    };

    let isValid = true;

    // Validate sender name
    if (!validateName(customization.senderName)) {
      newErrors.senderName = "Please enter your name.";
      isValid = false;
    }

    // Validate sender email
    const senderEmailValidation = validateEmail(customization.senderEmail);
    if (!senderEmailValidation.isValid) {
      newErrors.senderEmail =
        senderEmailValidation.message || "Please enter a valid email address.";
      isValid = false;
    }

    // Validate recipient name
    if (!validateName(customization.recipientName)) {
      newErrors.recipientName = "Please enter the recipient's name.";
      isValid = false;
    }

    // Validate recipient email
    const recipientEmailValidation = validateEmail(
      customization.recipientEmail,
    );
    if (!recipientEmailValidation.isValid) {
      newErrors.recipientEmail =
        recipientEmailValidation.message ||
        "Please enter the recipient's valid email address.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleContinue = () => {
    if (!validateForm()) {
      return;
    }

    // Ensure gift card amount is rounded and includes promo data
    const roundedGiftCard = {
      ...giftCard,
      amount: parseFloat(giftCard.amount).toFixed(2),
      originalAmount: parseFloat(giftCard.amount).toFixed(2),
      discount: discount.toFixed(2),
      finalAmount: finalAmount.toFixed(2),
      promoCode: promoApplied ? promoCode.toUpperCase() : null,
    };

    navigate("/gift-cards/payment", {
      state: { giftCard: roundedGiftCard, customization },
    });
  };

  const isValid =
    customization.senderName &&
    customization.senderEmail &&
    customization.recipientName &&
    customization.recipientEmail &&
    customization.deliveryDate &&
    !errors.senderName &&
    !errors.senderEmail &&
    !errors.recipientName &&
    !errors.recipientEmail;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDEFF7] to-white">
      {/* Header */}
      <div className="bg-white border-b border-[#D3D6E0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate("/gift-cards")}
            className="text-sm text-[#6E7180] hover:text-[#252F4F] mb-4 transition-colors">
            ← Back to Selection
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#252F4F] to-[#40424D] flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#252F4F]">
                Customize Your Gift Card
              </h1>
              <p className="text-[#6E7180] mt-1">
                Add a personal touch to your gift
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Sender Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#252F4F]">
                  Your Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="senderName" className="text-[#252F4F]">
                    Your Name
                  </Label>
                  <Input
                    id="senderName"
                    value={customization.senderName}
                    onChange={(e) => handleChange("senderName", e.target.value)}
                    placeholder="John Doe"
                    className={
                      errors.senderName
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }
                    aria-invalid={!!errors.senderName}
                  />
                  {errors.senderName && (
                    <p className="text-sm text-red-500 mt-1" role="alert">
                      {errors.senderName}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="senderEmail" className="text-[#252F4F]">
                    Your Email
                  </Label>
                  <Input
                    id="senderEmail"
                    type="email"
                    value={customization.senderEmail}
                    onChange={(e) =>
                      handleChange("senderEmail", e.target.value)
                    }
                    onBlur={(e) => {
                      // Validate on blur as well
                      if (e.target.value) {
                        const emailValidation = validateEmail(e.target.value);
                        if (!emailValidation.isValid) {
                          setErrors((prev) => ({
                            ...prev,
                            senderEmail:
                              emailValidation.message ||
                              "Please enter a valid email address.",
                          }));
                        }
                      }
                    }}
                    placeholder="john@example.com"
                    className={
                      errors.senderEmail
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }
                    aria-invalid={!!errors.senderEmail}
                    aria-describedby={
                      errors.senderEmail ? "senderEmail-error" : undefined
                    }
                  />
                  {errors.senderEmail && (
                    <p
                      id="senderEmail-error"
                      className="text-sm text-red-500 mt-1"
                      role="alert">
                      {errors.senderEmail}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recipient Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#252F4F]">
                  Recipient Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="recipientName" className="text-[#252F4F]">
                    Recipient Name
                  </Label>
                  <Input
                    id="recipientName"
                    value={customization.recipientName}
                    onChange={(e) =>
                      handleChange("recipientName", e.target.value)
                    }
                    placeholder="Jane Smith"
                    className={
                      errors.recipientName
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }
                    aria-invalid={!!errors.recipientName}
                  />
                  {errors.recipientName && (
                    <p className="text-sm text-red-500 mt-1" role="alert">
                      {errors.recipientName}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recipientEmail" className="text-[#252F4F]">
                    Recipient Email
                  </Label>
                  <Input
                    id="recipientEmail"
                    type="email"
                    value={customization.recipientEmail}
                    onChange={(e) =>
                      handleChange("recipientEmail", e.target.value)
                    }
                    onBlur={(e) => {
                      // Validate on blur as well
                      if (e.target.value) {
                        const emailValidation = validateEmail(e.target.value);
                        if (!emailValidation.isValid) {
                          setErrors((prev) => ({
                            ...prev,
                            recipientEmail:
                              emailValidation.message ||
                              "Please enter the recipient's valid email address.",
                          }));
                        }
                      }
                    }}
                    placeholder="jane@example.com"
                    className={
                      errors.recipientEmail
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }
                    aria-invalid={!!errors.recipientEmail}
                    aria-describedby={
                      errors.recipientEmail ? "recipientEmail-error" : undefined
                    }
                  />
                  {errors.recipientEmail && (
                    <p
                      id="recipientEmail-error"
                      className="text-sm text-red-500 mt-1"
                      role="alert">
                      {errors.recipientEmail}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Personal Message */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#252F4F]">
                  Personal Message (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={customization.personalMessage}
                  onChange={(e) =>
                    handleChange("personalMessage", e.target.value)
                  }
                  placeholder="Write a personal message..."
                  maxLength={500}
                  rows={4}
                />
                <p className="text-xs text-[#6E7180] mt-2">
                  {customization.personalMessage.length}/500 characters
                </p>
              </CardContent>
            </Card>

            {/* Delivery Date */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#252F4F]">Delivery Date</CardTitle>
              </CardHeader>
              <CardContent>
                <Label htmlFor="deliveryDate" className="text-[#252F4F]">
                  When should we send this gift card?
                </Label>
                <Input
                  id="deliveryDate"
                  type="date"
                  value={customization.deliveryDate}
                  onChange={(e) => handleChange("deliveryDate", e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="mt-2"
                />
              </CardContent>
            </Card>

            {/* Promo Code - Holiday Special */}
            <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-red-50">
              <CardHeader>
                <CardTitle className="text-[#252F4F] flex items-center gap-2">
                  🎄 Holiday Special
                  <span className="text-xs font-normal text-green-600 bg-green-100 px-2 py-1 rounded-full">
                    Valid until Dec 31
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-[#6E7180]">
                  Use code <strong className="text-green-600">HOLIDAYS</strong> for 15% off gift cards $100 or more!
                </p>
                <div className="flex gap-2">
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter promo code"
                    className="uppercase"
                    maxLength={10}
                  />
                  <Button
                    onClick={handleApplyPromo}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={!promoCode.trim()}>
                    Apply
                  </Button>
                </div>
                {promoError && (
                  <p className="text-sm text-red-600">{promoError}</p>
                )}
                {promoApplied && (
                  <p className="text-sm text-green-600 flex items-center gap-2">
                    <span>✓</span> Promo code applied! You save ${discount.toFixed(2)}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-[#252F4F]">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-[#EDEFF7] rounded-lg border border-[#D3D6E0]">
                  <p className="text-sm text-[#6E7180] mb-2">Gift Card Value</p>
                  <p className="text-3xl font-bold text-[#252F4F]">
                    ${displayAmount}
                  </p>
                  {giftCard.serviceType && (
                    <p className="text-sm text-[#6E7180] mt-2">
                      {giftCard.serviceType}
                    </p>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#6E7180]">Gift Card Value</span>
                    <span className="font-medium text-[#252F4F]">
                      ${displayAmount}
                    </span>
                  </div>
                  {promoApplied && discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Holiday Discount (15%)</span>
                      <span className="font-medium">
                        -${discount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-[#D3D6E0] pt-2">
                    <span className="font-bold text-[#252F4F]">Total Due</span>
                    <span className="font-bold text-[#98C454]">
                      ${finalAmount.toFixed(2)}
                    </span>
                  </div>
                  {promoApplied && (
                    <div className="text-xs text-green-600 text-center pt-1">
                      🎄 You're saving ${discount.toFixed(2)}!
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleContinue}
                  disabled={!isValid}
                  className="w-full bg-[#98C454] hover:bg-[#7FA84A] text-white disabled:opacity-50 disabled:cursor-not-allowed">
                  Continue to Payment
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
